import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, Link } from "react-router-dom";
import "./Auth.css";
import { useAuth } from '../../hooks/useAuth';

// safe access to environment variables
let API_BASE = '';
if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE) {
  API_BASE = process.env.REACT_APP_API_BASE;
}

// support create-react-app style env var fallback and a window global for runtime override
if (!API_BASE) API_BASE = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE) || '';
if (!API_BASE && typeof window !== 'undefined' && window.__API_BASE__) API_BASE = window.__API_BASE__;



export { API_BASE };

export default function Auth() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const initialTab = location.pathname.toLowerCase().includes("register")
    ? "register"
    : "login";

  const [tab, setTab] = useState(initialTab);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Role selection removed - strictly Patient registration here

  useEffect(() => {
    if (tab === "login" && !location.pathname.endsWith("/login")) {
      navigate("/login", { replace: true });
    }
    if (tab === "register" && !location.pathname.endsWith("/register")) {
      navigate("/register", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const { login } = useAuth();

  const onLogin = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    setIsLoading(true);
    const data = Object.fromEntries(new FormData(e.currentTarget).entries());
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, password: data.password }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Login failed');
      // save
      if (typeof login === 'function') login(json.user, json.token);
      else {
        localStorage.setItem('user', JSON.stringify(json.user));
        localStorage.setItem('token', json.token);
      }
      setSuccessMsg(t('auth.login_success', 'Login successful!'));
      if (json.user && json.user.role === 'admin') navigate('/admin');
      else if (json.user && json.user.role === 'doctor') navigate('/doctor');
      else navigate('/');
    } catch (err) {
      setErrorMsg(err.message || 'Login error');
    } finally {
      setIsLoading(false);
    }
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    const data = Object.fromEntries(new FormData(e.currentTarget).entries());
    if (data.password !== data.confirmPassword) {
      setErrorMsg(t('auth.password_mismatch', 'Passwords do not match'));
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        email: data.email,
        password: data.password,
        role: 'patient',
        fullName: data.fullName || '',
        gender: data.gender
      };
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Registration failed');
      // success
      setSuccessMsg(t('auth.register_success', 'Registration successful. Please log in.'));
      setTab('login');
      e.target.reset(); // clear form
    } catch (err) {
      setErrorMsg(err.message || 'Registration error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="auth-hero">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-brand">
            <Link to="/" className="brand-link" aria-label="Go home">Ta7arar</Link>
          </div>

          <div className="auth-tabs" role="tablist" aria-label="Authentication">
            <button
              role="tab"
              aria-selected={tab === "login"}
              className={`a-tab ${tab === "login" ? "active" : ""}`}
              onClick={() => { setTab("login"); setErrorMsg(""); setSuccessMsg(""); }}
              type="button"
            >
              {t("auth.login_tab")}
            </button>
            <button
              role="tab"
              aria-selected={tab === "register"}
              className={`a-tab ${tab === "register" ? "active" : ""}`}
              onClick={() => { setTab("register"); setErrorMsg(""); setSuccessMsg(""); }}
              type="button"
            >
              {t("auth.register_tab")}
            </button>
            <span className="a-slider" data-pos={tab} />
          </div>

          <h2 className="auth-title">
            {tab === "login" ? t("auth.sign_in_title") : t("auth.create_account_title")}
          </h2>

          {errorMsg && <div className="a-alert a-alert-danger">{errorMsg}</div>}
          {successMsg && <div className="a-alert a-alert-success">{successMsg}</div>}

          {tab === "login" ? (
            <form className="a-form" onSubmit={onLogin} noValidate>
              <label className="a-field">
                <span className="a-label">Email Address</span>
                <div className="a-input">
                  <input type="email" name="email" placeholder={t("auth.email")} required />
                  <span className="a-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5L4 8V6l8 5 8-5Z" fill="currentColor" />
                    </svg>
                  </span>
                </div>
              </label>

              <label className="a-field">
                <span className="a-label">{t("auth.password")}</span>
                <div className="a-input">
                  <input type="password" name="password" placeholder={t("auth.password")} required />
                  <span className="a-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path d="M17 9h-1V7A4 4 0 0 0 8 7v2H7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2Zm-6 0V7a2 2 0 0 1 4 0v2Z" fill="currentColor" />
                    </svg>
                  </span>
                </div>
              </label>

              <div className="a-row">
                <label className="a-check">
                  <input type="checkbox" name="remember" /> {t("auth.remember")}
                </label>
                {/* <a className="a-link" href="#">{t("auth.forgot_password")}</a> */}
              </div>

              <button className="a-btn a-primary" type="submit" disabled={isLoading}>
                {isLoading ? t("auth.loading", "Loading...") : t("auth.login_btn")}
              </button>



              <p className="a-foot">
                {t("auth.new_here")} <a href="/register">{t("auth.create_account_link")}</a>
              </p>
            </form>
          ) : (
            <form className="a-form" onSubmit={onRegister} noValidate>

              <div className="a-doctor-link" style={{ textAlign: 'center', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                <span style={{ color: '#666' }}>Are you a doctor? </span>
                <Link to="/join-medical-team" style={{ color: '#0056b3', fontWeight: '600', textDecoration: 'none' }}>
                  Join our Medical Team
                </Link>
              </div>
              <label className="a-field">
                <span className="a-label">{t("auth.full_name")}</span>
                <div className="a-input">
                  <input name="fullName" placeholder={t("auth.full_name")} required />
                  <span className="a-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-5.33 0-8 2.67-8 5v1h16v-1c0-2.33-2.67-5-8-5Z" fill="currentColor" />
                    </svg>
                  </span>
                </div>
              </label>

              <label className="a-field">
                <span className="a-label">{t("auth.email")}</span>
                <div className="a-input">
                  <input type="email" name="email" placeholder={t("auth.email")} required />
                  <span className="a-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5L4 8V6l8 5 8-5Z" fill="currentColor" />
                    </svg>
                  </span>
                </div>
              </label>

              <label className="a-field">
                <span className="a-label">{t("auth.password")}</span>
                <div className="a-input">
                  <input type="password" name="password" placeholder={t("auth.password")} required />
                  <span className="a-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path d="M17 9h-1V7A4 4 0 0 0 8 7v2H7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2Zm-6 0V7a2 2 0 0 1 4 0v2Z" fill="currentColor" />
                    </svg>
                  </span>
                </div>
              </label>

              <label className="a-field">
                <span className="a-label">{t("auth.confirm_password")}</span>
                <div className="a-input">
                  <input type="password" name="confirmPassword" placeholder={t("auth.confirm_password")} required />
                  <span className="a-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path d="M17 9h-1V7A4 4 0 0 0 8 7v2H7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2Zm-6 0V7a2 2 0 0 1 4 0v2Z" fill="currentColor" />
                    </svg>
                  </span>
                </div>
              </label>

              <label className="a-field">
                <span className="a-label">Gender</span>
                <div className="a-input" style={{ padding: '0 10px' }}>
                  <select name="gender" style={{ width: '100%', height: '100%', border: 'none', outline: 'none', background: 'transparent' }} required>
                    <option value="" disabled selected>Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </label>

              <label className="a-check">
                <input type="checkbox" name="terms" required /> {t("auth.terms_agree")}
              </label>

              <button className="a-btn a-primary" type="submit" disabled={isLoading}>
                {isLoading ? t("auth.loading", "Loading...") : t("auth.register_btn")}
              </button>



              <p className="a-foot">
                {t("auth.already_member")} <a href="/login">{t("auth.login_here")}</a>
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

